import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from './components/layout/Header';
import JobSeekers from './pages/JobSeekers';
import JobSeekerDetail from './pages/JobSeekerDetail';
import Profile from './pages/Profile';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Pricing from './pages/Pricing';
import Success from './pages/Success';
import Unsubscribe from './pages/Unsubscribe';
import SendEmail from './pages/SendEmail';
import Jobs from './pages/Jobs';
import JobDetail from './pages/JobDetail';
import OfflineIndicator from './components/common/OfflineIndicator';
import ErrorBoundary from './components/common/ErrorBoundary';
import RestrictedAccess from './components/auth/RestrictedAccess';
import AuthModal from './components/auth/AuthModal';
import RoleSelector from './components/auth/RoleSelector';
import OnboardingGuide from './components/auth/OnboardingGuide';
import { useUserProfile } from './hooks/useUserProfile';
import type { UserRole } from './types/profiles';

export default function App() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { needsRole, selectRole, dismissRoleSelector, loading: profileLoading } = useUserProfile();
  const [roleLoading, setRoleLoading] = useState(false);
  const [onboarding, setOnboarding] = useState<{ role: UserRole; companyName: string | null } | null>(null);

  return (
    <ErrorBoundary>
      <Helmet>
        <title>RanchWorkUSA.com - Ranch & Agriculture Jobs</title>
        <meta name="description" content="Find ranch and agriculture jobs across the country. Browse job seekers, post opportunities, and connect with qualified workers." />
        <meta property="og:title" content="RanchWorkUSA.com - Ranch & Agriculture Jobs" />
        <meta property="og:description" content="Find ranch and agriculture jobs across the country. Browse job seekers, post opportunities, and connect with qualified workers." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
      </Helmet>

      <RestrictedAccess>
      <div className="min-h-screen bg-western-tan-100 relative">
        <Header />
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Navigate to="/jobs" replace />} />
            <Route path="/home" element={<Navigate to="/jobs" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/job-seekers" element={<JobSeekers />} />
            <Route path="/job-seekers/:id" element={<JobSeekerDetail />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/success" element={<Success />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/jobs/:id" element={<JobDetail />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/send-email" element={<SendEmail />} />
          </Routes>
        </ErrorBoundary>
        <OfflineIndicator />

        <button
          id="auth-modal"
          className="hidden"
          onClick={() => setShowAuthModal(true)}
        />

        {showAuthModal && (
          <AuthModal onClose={() => setShowAuthModal(false)} />
        )}

        {needsRole && !profileLoading && (
          <RoleSelector
            onSelect={async (role, companyName) => {
              setRoleLoading(true);
              try {
                await selectRole(role, companyName);
                setOnboarding({ role, companyName: companyName || null });
              } catch {
                // silent
              } finally {
                setRoleLoading(false);
              }
            }}
            onClose={dismissRoleSelector}
            loading={roleLoading}
          />
        )}

        {onboarding && (
          <OnboardingGuide
            role={onboarding.role}
            companyName={onboarding.companyName}
            onClose={() => setOnboarding(null)}
          />
        )}
      </div>
      </RestrictedAccess>
    </ErrorBoundary>
  );
}
